import { Component, inject, Input, OnChanges, OnDestroy, OnInit, signal, SimpleChanges } from "@angular/core";
import { MatIconModule } from "@angular/material/icon";
import { BehaviorSubject, Subject } from "rxjs";
import { BetPlacementService } from "../../../../../../../shared/src/lib/services/bet-placement.service";
import { MatTooltipModule } from "@angular/material/tooltip";
import { BRunnerRowComponent } from "./b-runner-row/b-runner-row.component";
import { TranslateModule } from "@ngx-translate/core";
import { MarketService } from "../../../../../../../shared/src/lib/services/market.service";
import { LiveUpdateService } from "../../../../../../../shared/src/lib/services/live-update.service";
import { MarketOpsService } from "../market/market-ops.service";
import { OverlayComponent } from "../../../../components/overlay/overlay.component";
import { MatExpansionModule } from "@angular/material/expansion";
import { CashOutOfferComponent } from "../market/cash-out-offer/cash-out-offer.component";
import { BookmakerMarket, BookmakerRunner } from "../../../../../../../shared/src/lib/types/bet.type";
import { FeatureFlagService } from "../../../../../../../shared/src/lib/services/feature-flag.service";
import { AsyncPipe } from "@angular/common";
import { MyMarketsService } from "../../my-markets/my-markets.service";
import { Router, RouterLink } from "@angular/router";
import { BlockOverlayComponent } from "../../../../components/block-overlay/block-overlay.component";
import { WhiteLabelConfigService } from "../../../../../../../shared/src/lib/services/white-label-config.service";
import { takeUntil } from "rxjs/operators";
import { BetConfigComponent } from "../market/market-runners/bet-config/bet-config.component";

@Component({
  selector: "app-bookmaker",
  imports: [
    MatIconModule,
    MatTooltipModule,
    BRunnerRowComponent,
    TranslateModule,
    OverlayComponent,
    MatExpansionModule,
    CashOutOfferComponent,
    AsyncPipe,
    RouterLink,
    BlockOverlayComponent,
    BetConfigComponent,
  ],
  templateUrl: "./bookmaker.component.html",
  styleUrl: "./bookmaker.component.scss",
})
export class BookmakerComponent implements OnInit, OnDestroy, OnChanges {
  @Input() market: any;
  @Input() openDate: any;
  marketStatus = signal<string>("");
  updatedRunners = signal({});
  runnerUpdates = new BehaviorSubject<any>([]); // for cash-out
  readonly destroy$ = new Subject<void>();
  currentPlacement = new BehaviorSubject<any>(null);
  betPlacementService = inject(BetPlacementService);
  marketService = inject(MarketService);
  liveUpdateService = inject(LiveUpdateService);
  marketOps = inject(MarketOpsService);
  wlConfigService = inject(WhiteLabelConfigService);
  featureFlagService = inject(FeatureFlagService);
  myMarketsService = inject(MyMarketsService);
  router = inject(Router);
  status: string = "";
  events: any[] = [];
  allowPlacement = true;
  timeDiffCheckInterval: any = null;

  constructor() {}

  get isZeroPercentMatchOdds() {
    return this.market?.providerId === "0";
  }

  get isMarketOpen() {
    return !["INACTIVE", "CLOSED"].includes(this.market?.status);
  }

  get isMyMarketsPage() {
    return this.router.url.includes("content:my-markets");
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes["market"]) {
      this.getCurrentPlacement();
    }
  }

  ngOnInit() {
    this.initMarket();
  }

  getMarketUpdates(marketId: string) {
    this.liveUpdateService
      .getData("market", { field: "topic", value: marketId })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (data) => {
          if (data) this.handleMarketUpdate(data);
        },
      });
  }

  initMarket() {
    // console.log(this.market);
    if (this.market) {
      this.getMarketUpdates(this.market?.topic);
      this.marketStatus.set(this.market?.status);
      this.runnerUpdates.next(this.transformBmRunnersForCashOut(this.market?.runners));
      this.handleClosedMarket(this.market);
      this.checkPreMatchPlacement();
    }
  }

  checkPreMatchPlacement() {
    const placementPermitted = this.wlConfigService.showFeature("preMatchPlacement");
    this.allowPlacement = !placementPermitted && this.openDate ? this.wlConfigService.isWithin45Minutes(this.openDate) : true;

    if (!placementPermitted && this.openDate) {
      this.timeDiffCheckInterval = setInterval(() => {
        // console.log(this.wlConfigService.isWithin45Minutes(this.openDate));
        this.allowPlacement = this.wlConfigService.isWithin45Minutes(this.openDate);
      }, 5000);
    }
  }

  handleMarketUpdate(update: any) {
    if (this.market) {
      this.market["minBet"] = update?.minBet;
      this.market["maxBet"] = update?.maxBet;
    }
    this.handleClosedMarket(this.market);
    this.marketStatus.set(update?.status || "");
    if (update?.runners && update?.runners?.length > 0) {
      this.runnerUpdates.next(this.transformBmRunnersForCashOut(update?.runners));
      this.updatedRunners.set(this.marketOps.transformBmFancyRunners(update?.runners));
    }
  }

  transformBmRunnersForCashOut(runners: any[]) {
    return runners?.map((runner: BookmakerRunner) => ({
      id: runner?.selectionId,
      ex: {
        abp: Array.isArray(runner?.backPrice) ? runner?.backPrice : [runner?.backPrice],
        alp: Array.isArray(runner?.layPrice) ? runner?.layPrice : [runner?.layPrice],
      },
    }));
  }

  handleClosedMarket(market: BookmakerMarket) {
    if (market?.status === "CLOSED") {
      const { descriptionPrefix, eventId, providerId } = this.market;
      this.marketOps.removeMarket(market?.topic, "topic");
      this.myMarketsService.removeOpenBetsMarket(`${descriptionPrefix}_${eventId}_${providerId}`);
    }
  }

  getRunnerUpdate(selectionId: number) {
    const runners: any = this.updatedRunners();
    return runners?.[selectionId];
  }

  getCurrentPlacement() {
    this.betPlacementService
      .getCurrentPlacement()
      .pipe(takeUntil(this.destroy$))
      .subscribe((value: any) => {
        if (value && value?.MARKET_ID === this.market?.marketId) {
          // console.log("Gottcha with market id: ", value);
          this.currentPlacement.next(value);
        } else {
          this.currentPlacement.next(null);
        }
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    if (this.timeDiffCheckInterval) clearInterval(this.timeDiffCheckInterval);
  }
}
